import * as Ariakit from "@ariakit/react";
import { Warning } from "@phosphor-icons/react";
import {
  Banner,
  Button,
  Checkbox,
  NativeSelect,
  TextField,
  Tooltip,
  TooltipAnchor,
  TooltipArrow,
  TooltipProvider,
} from "@replicate/ui";
import Cookies from "js-cookie";
import { useCallback, useMemo, useRef, useState } from "react";
import { formatCurrency } from "../intl_helpers";
import type { Deployment, Hardware } from "../types";
import { route } from "../urls";
import { DeleteDeploymentButton } from "./delete-deployment-button";
import { DisableDeploymentUI } from "./deployment-disable-toggle";
import IconTooltip from "./icon-tooltip";
import { RenameDeploymentForm } from "./rename-deployment-form";
import VersionPicker, { type VersionForVersionPicker } from "./version-picker";

// Setup timeout limits for enterprise deployments (must match backend)
const MIN_SETUP_TIMEOUT_MINUTES = 1;
const MAX_SETUP_TIMEOUT_MINUTES = 180; // 3 hours

// Scale-in / scale-out stabilization window limits.
// Unlike setup timeout, 0 is a valid value.
const MIN_STABILIZATION_WINDOW_MINUTES = 0;
const MAX_STABILIZATION_WINDOW_MINUTES = 180; // 3 hours

// Scale-in / scale-out `policies` limits (must match backend:
// MAX_POLICIES_PER_DIRECTION/MIN_POLICY_PERIOD_SECONDS/
// MAX_POLICY_PERIOD_SECONDS/MAX_POLICY_VALUE_FOR_TYPE in
// replicate_web/models/models/scaling_config.py).
const MAX_POLICIES_PER_DIRECTION = 100;
const MIN_POLICY_PERIOD_SECONDS = 5;
const MAX_POLICY_PERIOD_SECONDS = 300;
const MAX_POLICY_VALUE_FOR_TYPE: Record<"count" | "percentage", number> = {
  count: 100,
  percentage: 1000,
};

// Parse a Django-serialized duration from ISO 8601 duration format (e.g.,
// "P0DT00H10M00S"). This is how Django's DurationField / DjangoJSONEncoder
// serialize timedelta values.
const parseDjangoDurationSeconds = (duration: string | null): number => {
  if (!duration) return 0;

  // Parse ISO 8601 duration: P[n]DT[n]H[n]M[n]S
  const regex = /P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/;
  const match = duration.match(regex);
  if (match) {
    const days = Number.parseInt(match[1] || "0", 10);
    const hours = Number.parseInt(match[2] || "0", 10);
    const minutes = Number.parseInt(match[3] || "0", 10);
    const seconds = Number.parseFloat(match[4] || "0");
    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
  }
  return 0;
};

// Initialize a value/unit pair with a smart default unit based on the current
// duration value.
const initializeDurationField = (
  duration: string | null
): { value: string; unit: "minutes" | "hours" } => {
  if (!duration) return { value: "", unit: "minutes" };
  const totalSeconds = parseDjangoDurationSeconds(duration);
  const totalMinutes = Math.floor(totalSeconds / 60);

  // If evenly divisible by 60 and >= 60, show in hours
  if (totalMinutes >= 60 && totalMinutes % 60 === 0) {
    return { value: String(totalMinutes / 60), unit: "hours" };
  }
  return { value: String(totalMinutes), unit: "minutes" };
};

// Convert a total number of seconds into Django's DurationField submission
// format: "D HH:MM:SS".
const formatDjangoDuration = (totalSeconds: number): string => {
  const days = Math.floor(totalSeconds / 86400);
  const remainingAfterDays = totalSeconds % 86400;
  const hours = Math.floor(remainingAfterDays / 3600);
  const remainingAfterHours = remainingAfterDays % 3600;
  const minutes = Math.floor(remainingAfterHours / 60);
  const seconds = Math.floor(remainingAfterHours % 60);

  return `${days} ${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

function HardwareOption({
  hardware,
  selected,
}: {
  hardware: Hardware;
  selected: boolean;
}) {
  const costPerHour = useMemo(
    () =>
      hardware.cost_per_second_dollars
        ? Number(hardware.cost_per_second_dollars) * 60 * 60
        : null,
    [hardware.cost_per_second_dollars]
  );
  const [focusVisible, setFocusVisible] = useState(false);

  if (hardware.public_sku == null) {
    return null;
  }

  return (
    <div
      className="relative group"
      data-selected={selected}
      data-focus-visible={focusVisible}
    >
      <label
        className="block cursor-pointer hover:border-r8-gray-12 group group-data-[selected=true]:border-black border w-full appearance-none text-left p-3"
        title={hardware.display_name}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 pointer-events-none">
              <Ariakit.Radio
                className="accent-black focus:outline-none"
                onFocusVisible={() => setFocusVisible(true)}
                onBlur={() => setFocusVisible(false)}
                name="hardware"
                id={hardware.public_sku}
                value={hardware.public_sku}
              />
            </div>
            <div>
              <span className="text-base font-semibold leading-none">
                {hardware.display_name}
              </span>
              <div>
                <dl className="text-r8-gray-11 flex flex-col md:flex-row md:items-center gap-1 text-sm mt-1">
                  {hardware.cpu_count != null && (
                    <>
                      <dt hidden>CPU</dt>
                      <dd>{hardware.cpu_count}x CPU</dd>
                      {(hardware.gpu_ram != null || hardware.ram != null) && (
                        <span className="hidden md:inline" aria-hidden>
                          /
                        </span>
                      )}
                    </>
                  )}
                  {hardware.gpu_ram != null && (
                    <>
                      <dt hidden>GPU</dt>
                      <dd>{hardware.gpu_ram}GB GPU RAM</dd>
                      {hardware.ram != null && (
                        <span className="hidden md:inline" aria-hidden>
                          /
                        </span>
                      )}
                    </>
                  )}
                  {hardware.ram != null && (
                    <>
                      <dt hidden>RAM</dt>
                      <dd>{hardware.ram}GB RAM</dd>
                    </>
                  )}
                </dl>
              </div>
            </div>
          </div>
          {hardware.cost_per_second_dollars != null && (
            <div className="text-right text-base space-y-1">
              <p className="tabular-nums">
                ${Number(hardware.cost_per_second_dollars).toFixed(6)}/sec
              </p>
              <p className="tabular-nums text-r8-gray-11">
                {/* biome-ignore lint/style/noNonNullAssertion: costPerHour is derived from option.unit_cost_per_second. */}
                ${costPerHour!.toFixed(2)}/hour
              </p>
            </div>
          )}
        </div>
      </label>
    </div>
  );
}

export function formatHardwareDailyCost({
  hardware,
  count,
}: {
  hardware: Hardware | null;
  count: number | "";
}): {
  raw: string;
  formatted: string;
} {
  if (typeof count !== "number") {
    return { raw: "", formatted: "-" };
  }

  const costPerSecond = hardware?.cost_per_second_dollars;

  if (costPerSecond == null) {
    return { raw: "", formatted: "-" };
  }

  const cost = Number(costPerSecond) * count * 60 * 60 * 24;
  return {
    raw: cost.toFixed(2),
    formatted: formatCurrency(cost),
  };
}

type StabilizationWindowDirection = "scale_in" | "scale_out";

// Manages the opt-in / value / unit state for a single scale-in or scale-out
// stabilization window control. The input starts disabled, pre-filled with
// the effective default (if any) so the customer can see what they'd be
// overriding; it only becomes editable once they opt in via the paired
// checkbox.
function useStabilizationWindowControl({
  current,
  defaultDuration,
}: {
  current: string | null;
  defaultDuration: string | null;
}) {
  const initial = useMemo(
    () => initializeDurationField(current ?? defaultDuration),
    [current, defaultDuration]
  );

  const [enabled, setEnabled] = useState(current !== null);
  const [value, setValue] = useState(initial.value);
  const [unit, setUnit] = useState<"minutes" | "hours">(initial.unit);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = useCallback(
    (
      inputElement: HTMLInputElement,
      rawValue: string,
      currentUnit: "minutes" | "hours"
    ) => {
      const val = Number.parseFloat(rawValue);

      // NOTE: only bail out for NaN here. Negative values are
      // a real constraint violation (min is 0), not "nothing to validate
      // yet" -- they must fall through to the range check below so
      // `setCustomValidity` actually flags them instead of silently
      // clearing any existing error.
      if (Number.isNaN(val)) {
        inputElement.setCustomValidity("");
        return;
      }

      const totalMinutes = currentUnit === "hours" ? val * 60 : val;

      if (totalMinutes < MIN_STABILIZATION_WINDOW_MINUTES) {
        inputElement.setCustomValidity(
          `Stabilization window must be at least ${MIN_STABILIZATION_WINDOW_MINUTES} minutes`
        );
      } else if (totalMinutes > MAX_STABILIZATION_WINDOW_MINUTES) {
        const maxValue = MAX_STABILIZATION_WINDOW_MINUTES / 60;
        const maxUnit = maxValue === 1 ? "hour" : "hours";
        inputElement.setCustomValidity(
          `Stabilization window must not exceed ${maxValue} ${maxUnit}`
        );
      } else {
        inputElement.setCustomValidity("");
      }
    },
    []
  );

  const onValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setValue(newValue);
      validate(event.currentTarget, newValue, unit);
    },
    [unit, validate]
  );

  const onUnitChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newUnit = event.target.value as "minutes" | "hours";
      setUnit(newUnit);

      const inputElement = inputRef.current;
      if (!inputElement) return;
      validate(inputElement, value, newUnit);
    },
    [value, validate]
  );

  const onEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEnabled(event.target.checked);
    },
    []
  );

  return {
    enabled,
    onEnabledChange,
    value,
    unit,
    inputRef,
    onValueChange,
    onUnitChange,
    defaultValue: initializeDurationField(defaultDuration),
  };
}

function StabilizationWindowField({
  direction,
  label,
  description,
  control,
  onWheel,
}: {
  direction: StabilizationWindowDirection;
  label: string;
  description: string;
  control: ReturnType<typeof useStabilizationWindowControl>;
  onWheel: (event: React.WheelEvent<HTMLInputElement>) => void;
}) {
  const enabledCheckboxId = `${direction}_stabilization_window_enabled_checkbox`;
  const hasDefault = control.defaultValue.value !== "";

  return (
    <div>
      <div className="flex items-center gap-2 mb-05lh">
        <Checkbox
          type="checkbox"
          checked={control.enabled}
          onChange={control.onEnabledChange}
          name={`${direction}_stabilization_window_enabled`}
          id={enabledCheckboxId}
        />
        <label
          htmlFor={enabledCheckboxId}
          className="flex items-center gap-1 text-base font-semibold"
        >
          Override {label.toLowerCase()}
          <IconTooltip
            icon="Question"
            weight="light"
            tooltipText={`${description} Range: ${MIN_STABILIZATION_WINDOW_MINUTES} minutes to ${
              MAX_STABILIZATION_WINDOW_MINUTES / 60
            } ${
              MAX_STABILIZATION_WINDOW_MINUTES / 60 === 1 ? "hour" : "hours"
            }.`}
            anchorClass="flex-shrink-0 inline"
          />
        </label>
      </div>

      <div className="flex items-center gap-2">
        <TextField
          id={`${direction}_stabilization_window`}
          ref={control.inputRef}
          type="number"
          name={`${direction}_stabilization_window`}
          value={control.value}
          onChange={control.onValueChange}
          placeholder="Enter duration"
          disabled={!control.enabled}
          aria-label={`${label} value`}
          className="w-32 block bg-white dark:bg-r8-gray-1 border border-r8-gray-12 p-05lh disabled:opacity-50"
          step={control.unit === "hours" ? 0.5 : 1}
          onWheel={onWheel}
        />
        <NativeSelect
          id={`${direction}_stabilization_window_unit`}
          name={`${direction}_stabilization_window_unit`}
          value={control.unit}
          onChange={control.onUnitChange}
          disabled={!control.enabled}
          aria-label={`${label} unit`}
          className="block bg-white dark:bg-r8-gray-1 border border-r8-gray-12 p-05lh disabled:opacity-50"
        >
          <option value="minutes">
            {control.value === "1" ? "minute" : "minutes"}
          </option>
          <option value="hours">
            {control.value === "1" ? "hour" : "hours"}
          </option>
        </NativeSelect>
      </div>
      <p className="text-sm text-r8-gray-11 mt-05lh">
        {hasDefault ? (
          <>
            Current default: {control.defaultValue.value}{" "}
            {control.defaultValue.unit}.
          </>
        ) : (
          "No default is currently configured."
        )}
      </p>
    </div>
  );
}

type ScalingPolicyType = "count" | "percentage";

interface ScalingPolicy {
  type: ScalingPolicyType;
  period_seconds: number;
  value: number;
}

const DEFAULT_NEW_POLICY: ScalingPolicy = {
  type: "count",
  period_seconds: 60,
  value: 1,
};

// Manages the opt-in / row-list state for a single scale-in or scale-out
// `policies` control. Like `useStabilizationWindowControl`, the rows start
// disabled, pre-filled with the effective default (if any) so the customer
// can see what they'd be overriding; they only become editable once the
// customer opts in via the paired checkbox.
function useScalingPoliciesControl({
  current,
  defaultPolicies,
}: {
  current: ScalingPolicy[] | null;
  defaultPolicies: ScalingPolicy[] | null;
}) {
  const [enabled, setEnabled] = useState(current !== null);
  const [rows, setRows] = useState<ScalingPolicy[]>(
    current ?? defaultPolicies ?? []
  );

  const onEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEnabled(event.target.checked);
    },
    []
  );

  const addRow = useCallback(() => {
    setRows((prev) =>
      prev.length >= MAX_POLICIES_PER_DIRECTION
        ? prev
        : [...prev, { ...DEFAULT_NEW_POLICY }]
    );
  }, []);

  const removeRow = useCallback((index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateRow = useCallback(
    (index: number, patch: Partial<ScalingPolicy>) => {
      setRows((prev) =>
        prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
      );
    },
    []
  );

  return {
    enabled,
    onEnabledChange,
    rows,
    addRow,
    removeRow,
    updateRow,
    defaultPolicies,
  };
}

function ScalingPoliciesField({
  direction,
  label,
  description,
  control,
}: {
  direction: StabilizationWindowDirection;
  label: string;
  description: string;
  control: ReturnType<typeof useScalingPoliciesControl>;
}) {
  const enabledCheckboxId = `${direction}_policies_enabled_checkbox`;
  const hasDefault =
    control.defaultPolicies !== null && control.defaultPolicies.length > 0;
  const atMaxRows = control.rows.length >= MAX_POLICIES_PER_DIRECTION;

  return (
    <div>
      <div className="flex items-center gap-2 mb-05lh">
        <Checkbox
          type="checkbox"
          checked={control.enabled}
          onChange={control.onEnabledChange}
          name={`${direction}_policies_enabled`}
          id={enabledCheckboxId}
        />
        <label
          htmlFor={enabledCheckboxId}
          className="flex items-center gap-1 text-base font-semibold"
        >
          Override {label.toLowerCase()} policies
          <IconTooltip
            icon="Question"
            weight="light"
            tooltipText={
              <>
                {description}
                <br />
                Each policy caps scaling per period of{" "}
                {MIN_POLICY_PERIOD_SECONDS}-{MAX_POLICY_PERIOD_SECONDS}s up to{" "}
                {MAX_POLICY_VALUE_FOR_TYPE.count} instances or{" "}
                {MAX_POLICY_VALUE_FOR_TYPE.percentage}%
              </>
            }
            anchorClass="flex-shrink-0 inline"
          />
        </label>
      </div>

      <div className="space-y-2">
        {control.rows.map((row, index) => {
          const maxValue = MAX_POLICY_VALUE_FOR_TYPE[row.type];
          return (
            <div key={index} className="flex items-center gap-2">
              <NativeSelect
                id={`${direction}_policies_${index}_type`}
                name={`${direction}_policies_${index}_type`}
                aria-label={`${label} policy ${index + 1} type`}
                value={row.type}
                disabled={!control.enabled}
                onChange={(event) =>
                  control.updateRow(index, {
                    type: event.target.value as ScalingPolicyType,
                  })
                }
                className="block bg-white dark:bg-r8-gray-1 border border-r8-gray-12 p-05lh disabled:opacity-50"
              >
                <option value="count">instances</option>
                <option value="percentage">percent</option>
              </NativeSelect>
              {/* `TextField` renders a nested `.r8-form-group` wrapper chain;
              `className` only reaches the innermost `<input>`, which isn't
              the box that's actually a flex item in this row. Wrap it in our
              own fixed-width, non-shrinking div so `<input type="number">`'s
              own `max`-attribute-driven intrinsic sizing (see
              https://www.philiprenich.com/blog/2022/10/the-width-of-number-inputs/)
              can't leak through and change the row's layout. */}
              <div className="w-24 shrink-0">
                <TextField
                  id={`${direction}_policies_${index}_value`}
                  type="number"
                  aria-label={`${label} policy ${index + 1} value`}
                  value={row.value}
                  disabled={!control.enabled}
                  min={0}
                  max={maxValue}
                  step={1}
                  onChange={(event) =>
                    control.updateRow(index, {
                      value: Number.parseInt(event.target.value, 10) || 0,
                    })
                  }
                  className="block w-full bg-white dark:bg-r8-gray-1 border border-r8-gray-12 p-05lh disabled:opacity-50"
                />
              </div>
              <span className="text-sm text-r8-gray-11">per</span>
              <div className="w-24 shrink-0">
                <TextField
                  id={`${direction}_policies_${index}_period_seconds`}
                  type="number"
                  aria-label={`${label} policy ${index + 1} period (seconds)`}
                  value={row.period_seconds}
                  disabled={!control.enabled}
                  min={MIN_POLICY_PERIOD_SECONDS}
                  max={MAX_POLICY_PERIOD_SECONDS}
                  step={1}
                  onChange={(event) =>
                    control.updateRow(index, {
                      period_seconds:
                        Number.parseInt(event.target.value, 10) || 0,
                    })
                  }
                  className="block w-full bg-white dark:bg-r8-gray-1 border border-r8-gray-12 p-05lh disabled:opacity-50"
                />
              </div>
              <span className="text-sm text-r8-gray-11">seconds</span>
              <Button
                type="button"
                disabled={!control.enabled}
                onClick={() => control.removeRow(index)}
                className="r8-btn r8-btn--sm r8-btn--outlined"
              >
                Remove
              </Button>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        disabled={!control.enabled || atMaxRows}
        onClick={control.addRow}
        className="r8-btn r8-btn--sm r8-btn--outlined mt-2"
      >
        Add policy
      </Button>

      <p className="text-sm text-r8-gray-11 mt-05lh">
        {hasDefault ? (
          <>
            Current default has {control.defaultPolicies?.length}{" "}
            {control.defaultPolicies?.length === 1 ? "policy" : "policies"}.
          </>
        ) : (
          "No default policies are currently configured."
        )}
      </p>
    </div>
  );
}

export default function DeploymentSettingsForm({
  currentVersion,
  deleteDisabledReason,
  deployment,
  hardwareOptions,
  renameDisabledReason,
  versions,
  advancedSettingsAvailable,
  setupAlertsEnabled,
  setupAlertsEmail,
  currentSetupTimeout,
  currentScaleInStabilizationWindow,
  currentScaleOutStabilizationWindow,
  defaultScaleInStabilizationWindow,
  defaultScaleOutStabilizationWindow,
  currentScaleInPolicies,
  currentScaleOutPolicies,
  defaultScaleInPolicies,
  defaultScaleOutPolicies,
}: {
  currentVersion: VersionForVersionPicker;
  deleteDisabledReason: string;
  deployment: Deployment;
  hardwareOptions: Hardware[];
  renameDisabledReason: string;
  versions: VersionForVersionPicker[];
  advancedSettingsAvailable: boolean;
  setupAlertsEnabled: boolean;
  setupAlertsEmail: string | null;
  currentSetupTimeout: string | null;
  currentScaleInStabilizationWindow: string | null;
  currentScaleOutStabilizationWindow: string | null;
  defaultScaleInStabilizationWindow: string | null;
  defaultScaleOutStabilizationWindow: string | null;
  currentScaleInPolicies: ScalingPolicy[] | null;
  currentScaleOutPolicies: ScalingPolicy[] | null;
  defaultScaleInPolicies: ScalingPolicy[] | null;
  defaultScaleOutPolicies: ScalingPolicy[] | null;
}) {
  // Note that we're controlling this value
  // only to determine whether to display a warning
  // about incremental rollouts.
  // We don't actually use this value in the form submission,
  // but rather read directly from the FormData.
  const [version, setVersion] = useState(currentVersion);

  const [hardwareSku, setHardwareSku] = useState(
    deployment.current_release.configuration.hardware
  );
  const hardware = useMemo(
    () =>
      hardwareOptions.find(({ public_sku: value }) => value === hardwareSku) ??
      null,
    [hardwareOptions, hardwareSku]
  );
  const [minInstancesValue, setMinInstancesValue] = useState<number | "">(
    deployment.current_release.configuration.min_instances ?? ""
  );
  const [maxInstancesValue, setMaxInstancesValue] = useState<number | "">(
    deployment.current_release.configuration.max_instances ?? ""
  );
  const [errorDetails, setErrorDetails] = useState<Record<
    string,
    string[]
  > | null>(null);

  const limits = useMemo(
    () => ({
      minInstances: {
        min: Math.min(
          0,
          deployment.current_release.configuration.min_instances ??
            Number.POSITIVE_INFINITY
        ),
        currentMax: Math.min(
          deployment._extras.allowed_min_instances ??
            hardware?.default_allowed_min_instances_for_deployments ??
            0,
          typeof maxInstancesValue === "number"
            ? maxInstancesValue
            : Number.POSITIVE_INFINITY
        ),
        trueMax:
          deployment._extras.allowed_min_instances ??
          hardware?.default_allowed_min_instances_for_deployments ??
          0,
      },
      maxInstances: {
        min: Math.max(
          Math.min(
            0,
            deployment.current_release.configuration.max_instances ??
              Number.POSITIVE_INFINITY
          ),
          typeof minInstancesValue === "number" ? minInstancesValue : 0
        ),
        max:
          deployment._extras.allowed_max_instances ??
          hardware?.default_allowed_max_instances_for_deployments ??
          0,
      },
    }),
    [
      deployment._extras.allowed_max_instances,
      deployment._extras.allowed_min_instances,
      deployment.current_release.configuration.max_instances,
      deployment.current_release.configuration.min_instances,
      hardware?.default_allowed_max_instances_for_deployments,
      hardware?.default_allowed_min_instances_for_deployments,
      maxInstancesValue,
      minInstancesValue,
    ]
  );

  const onMinInstancesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number.parseInt(event.target.value, 10);
      setMinInstancesValue(Number.isInteger(val) && val >= 0 ? val : "");
    },
    []
  );

  const onMaxInstancesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number.parseInt(event.target.value, 10);

      setMaxInstancesValue(Number.isInteger(val) && val >= 0 ? val : "");

      // Set custom error message on the max instance when the number
      // is less than the min instances value.
      event.currentTarget.setCustomValidity(
        minInstancesValue !== "" && val < minInstancesValue
          ? "Maximum instances must be a larger number than minimum instances"
          : ""
      );
    },
    [minInstancesValue]
  );

  const showIncreaseCapacityCTA = useMemo(
    () =>
      (minInstancesValue !== "" &&
        minInstancesValue >= limits.minInstances.trueMax) ||
      (maxInstancesValue !== "" &&
        maxInstancesValue >= limits.maxInstances.max),
    [
      limits.maxInstances.max,
      limits.minInstances.trueMax,
      maxInstancesValue,
      minInstancesValue,
    ]
  );

  const minCost = useMemo(
    () =>
      formatHardwareDailyCost({
        hardware,
        count: minInstancesValue,
      }),
    [hardware, minInstancesValue]
  );
  const maxCost = useMemo(
    () =>
      formatHardwareDailyCost({
        hardware,
        count: maxInstancesValue,
      }),
    [hardware, maxInstancesValue]
  );

  // Prevent the input value from changing on wheel events
  const onWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };

  const [hasSetupAlertsEnabled, setSetupAlertsEnabled] =
    useState(setupAlertsEnabled);

  const initialSetupTimeout = useMemo(
    () => initializeDurationField(currentSetupTimeout),
    [currentSetupTimeout]
  );
  const [setupTimeoutValue, setSetupTimeoutValue] = useState<string>(
    initialSetupTimeout.value
  );
  const [setupTimeoutUnit, setSetupTimeoutUnit] = useState<"minutes" | "hours">(
    initialSetupTimeout.unit
  );

  const setupTimeoutInputRef = useRef<HTMLInputElement>(null);

  const scaleInStabilization = useStabilizationWindowControl({
    current: currentScaleInStabilizationWindow,
    defaultDuration: defaultScaleInStabilizationWindow,
  });
  const scaleOutStabilization = useStabilizationWindowControl({
    current: currentScaleOutStabilizationWindow,
    defaultDuration: defaultScaleOutStabilizationWindow,
  });

  const scaleInPolicies = useScalingPoliciesControl({
    current: currentScaleInPolicies,
    defaultPolicies: defaultScaleInPolicies,
  });
  const scaleOutPolicies = useScalingPoliciesControl({
    current: currentScaleOutPolicies,
    defaultPolicies: defaultScaleOutPolicies,
  });

  const onFormSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!event.currentTarget.checkValidity()) {
        const form = event.currentTarget;
        const maxInstancesEl = form.querySelector<HTMLInputElement>(
          "[name=max_instances]"
        );

        // Ensure that we show this error on the max_instances field
        // before the min_instances errors. Otherwise the browser
        // will show the first errored field and result in a confusing
        // error message on the min_instances field.
        if (maxInstancesEl && maxInstancesValue < minInstancesValue) {
          Promise.resolve().then(() => maxInstancesEl.reportValidity());
          return;
        }

        Promise.resolve().then(() => form.reportValidity());
        return;
      }

      const formData = new FormData(event.currentTarget);

      // Only include setup_timeout if it has changed from the initial value
      // Normalize values for comparison to handle different string representations
      const currentValueNum = Number.parseFloat(setupTimeoutValue);
      const initialValueNum = Number.parseFloat(initialSetupTimeout.value);

      const hasValueChanged =
        // Both are valid numbers - compare numerically
        !Number.isNaN(currentValueNum) && !Number.isNaN(initialValueNum)
          ? currentValueNum !== initialValueNum
          : // Otherwise compare as strings (handles empty strings, etc.)
            setupTimeoutValue !== initialSetupTimeout.value;

      const hasSetupTimeoutChanged =
        hasValueChanged || setupTimeoutUnit !== initialSetupTimeout.unit;

      if (!hasSetupTimeoutChanged) {
        formData.delete("setup_timeout");
        formData.delete("setup_timeout_unit");
      } else {
        // Convert setup_timeout to Django's duration format: "DD HH:MM:SS"
        const value = Number.parseFloat(setupTimeoutValue);
        if (!Number.isNaN(value) && value > 0) {
          const totalSeconds =
            setupTimeoutUnit === "hours" ? value * 3600 : value * 60;
          formData.set("setup_timeout", formatDjangoDuration(totalSeconds));
        }
      }

      // Reference only the individual primitive fields captured in this
      // callback's dependency array (not the whole `scaleInStabilization`/
      // `scaleOutStabilization` objects), so the exhaustive-deps lint rule
      // can match captures to dependencies precisely.
      for (const { direction, enabled, rawValue, unit } of [
        {
          direction: "scale_in" as const,
          enabled: scaleInStabilization.enabled,
          rawValue: scaleInStabilization.value,
          unit: scaleInStabilization.unit,
        },
        {
          direction: "scale_out" as const,
          enabled: scaleOutStabilization.enabled,
          rawValue: scaleOutStabilization.value,
          unit: scaleOutStabilization.unit,
        },
      ]) {
        if (!enabled) {
          // Not opted in: don't submit a duration value. The `_enabled`
          // checkbox is simply absent from FormData when unchecked, which
          // the backend treats as "remove any existing override."
          formData.delete(`${direction}_stabilization_window`);
          continue;
        }

        const value = Number.parseFloat(rawValue);
        const totalSeconds = Number.isNaN(value)
          ? 0
          : unit === "hours"
            ? value * 3600
            : value * 60;
        formData.set(
          `${direction}_stabilization_window`,
          formatDjangoDuration(totalSeconds)
        );
      }

      for (const { direction, enabled, rows } of [
        {
          direction: "scale_in" as const,
          enabled: scaleInPolicies.enabled,
          rows: scaleInPolicies.rows,
        },
        {
          direction: "scale_out" as const,
          enabled: scaleOutPolicies.enabled,
          rows: scaleOutPolicies.rows,
        },
      ]) {
        if (!enabled) {
          // Not opted in: don't submit a policies list. The `_enabled`
          // checkbox is simply absent from FormData when unchecked, which
          // the backend treats as "remove any existing override."
          formData.delete(`${direction}_policies`);
          continue;
        }

        formData.set(`${direction}_policies`, JSON.stringify(rows));
      }

      const response = await fetch(
        route("api_deployment_edit_or_delete", {
          username: deployment.owner,
          name: deployment.name,
        }),
        {
          method: "PATCH",
          headers: {
            "X-CSRFToken": Cookies.get("csrftoken") ?? "",
          },
          body: formData,
        }
      );

      if (response.ok) {
        const { next } = await response.json();
        window.location.href = next;
        return;
      }

      if (response.status === 400) {
        const { detail } = await response.json();
        setErrorDetails(detail);
      } else {
        setErrorDetails({
          Error: ["An unexpected error occurred, please try again."],
        });
      }
    },
    [
      deployment,
      maxInstancesValue,
      minInstancesValue,
      setupTimeoutValue,
      setupTimeoutUnit,
      initialSetupTimeout.value,
      initialSetupTimeout.unit,
      scaleInStabilization.enabled,
      scaleInStabilization.value,
      scaleInStabilization.unit,
      scaleOutStabilization.enabled,
      scaleOutStabilization.value,
      scaleOutStabilization.unit,
      scaleInPolicies.enabled,
      scaleInPolicies.rows,
      scaleOutPolicies.enabled,
      scaleOutPolicies.rows,
    ]
  );

  const errorDetailsList = errorDetails
    ? Object.entries(errorDetails).map(([key, value]) => {
        return (
          <li key={key}>
            {key}: {value[0]}
          </li>
        );
      })
    : null;

  const hasPinnedMaxMinInstances =
    maxInstancesValue !== "" &&
    maxInstancesValue === limits.minInstances.currentMax;

  const hasPickedNewVersion = version.id !== currentVersion.id;
  const hasPickedNewHardware =
    hardwareSku !== deployment.current_release.configuration.hardware;

  let saveBanner: React.ReactNode = null;

  if (deployment._extras.disabled) {
    saveBanner = (
      <Banner
        severity="warning"
        icon={<Warning />}
        condensed
        heading="Changes will not take immediate effect"
        description={
          <div className="space-y-05lh">
            <p>
              {deployment._extras.disabled_by_system ? "We " : "You "}
              have disabled this deployment. Any changes will be saved but will
              not take effect until
              {deployment._extras.disabled_by_system ? " we " : " you "}
              re-enable the deployment.
            </p>
          </div>
        }
      />
    );
  } else if (hasPickedNewVersion || hasPickedNewHardware) {
    saveBanner = (
      <Banner
        severity="warning"
        icon={<Warning />}
        condensed
        heading="These changes will trigger an incremental rollout"
        description={
          <div className="space-y-05lh">
            <p>
              While this change rolls out, some predictions will use the old
              settings, while others will start to use the new ones. Assuming
              the new settings are compatible with your application, you
              shouldn’t experience any downtime while this happens.
            </p>
            <p>
              Note: We might exceed the specified maximum number of online
              instances during an incremental rollout. This is because we start
              a new instance and wait for it to be ready before shutting down an
              old one.
            </p>
          </div>
        }
      />
    );
  }

  const onSetupTimeoutChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setSetupTimeoutValue(newValue);

      const val = Number.parseFloat(newValue);

      // NOTE: only bail out for NaN here. Zero and negative
      // values are real constraint violations (min is
      // MIN_SETUP_TIMEOUT_MINUTES, not 0), not "nothing to validate yet" --
      // they must fall through to the range check below so
      // `setCustomValidity` actually flags them instead of silently
      // clearing any existing error.
      if (Number.isNaN(val)) {
        event.currentTarget.setCustomValidity("");
        return;
      }

      const totalMinutes = setupTimeoutUnit === "hours" ? val * 60 : val;

      if (totalMinutes < MIN_SETUP_TIMEOUT_MINUTES) {
        const minUnit = MIN_SETUP_TIMEOUT_MINUTES === 1 ? "minute" : "minutes";
        event.currentTarget.setCustomValidity(
          `Setup timeout must be at least ${MIN_SETUP_TIMEOUT_MINUTES} ${minUnit}`
        );
      } else if (totalMinutes > MAX_SETUP_TIMEOUT_MINUTES) {
        const maxValue = MAX_SETUP_TIMEOUT_MINUTES / 60;
        const maxUnit = maxValue === 1 ? "hour" : "hours";
        event.currentTarget.setCustomValidity(
          `Setup timeout must not exceed ${maxValue} ${maxUnit}`
        );
      } else {
        event.currentTarget.setCustomValidity("");
      }
    },
    [setupTimeoutUnit]
  );

  const onSetupTimeoutUnitChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newUnit = event.target.value as "minutes" | "hours";
      setSetupTimeoutUnit(newUnit);

      // Re-validate the input with the new unit
      const inputElement = setupTimeoutInputRef.current;
      if (!inputElement) return;

      const val = Number.parseFloat(setupTimeoutValue);

      // NOTE: see the matching comment in onSetupTimeoutChange
      // above -- only bail out for NaN, not for zero/negative values.
      if (Number.isNaN(val)) {
        inputElement.setCustomValidity("");
        return;
      }

      const totalMinutes = newUnit === "hours" ? val * 60 : val;

      if (totalMinutes < MIN_SETUP_TIMEOUT_MINUTES) {
        const minUnit = MIN_SETUP_TIMEOUT_MINUTES === 1 ? "minute" : "minutes";
        inputElement.setCustomValidity(
          `Setup timeout must be at least ${MIN_SETUP_TIMEOUT_MINUTES} ${minUnit}`
        );
      } else if (totalMinutes > MAX_SETUP_TIMEOUT_MINUTES) {
        const maxValue = MAX_SETUP_TIMEOUT_MINUTES / 60;
        const maxUnit = maxValue === 1 ? "hour" : "hours";
        inputElement.setCustomValidity(
          `Setup timeout must not exceed ${maxValue} ${maxUnit}`
        );
      } else {
        inputElement.setCustomValidity("");
      }
    },
    [setupTimeoutValue]
  );

  let enterpriseFeatures: React.ReactNode = null;
  // Gates the whole "Advanced settings" section: true for an active
  // enterprise contract *or* an internal staff/superuser account (computed
  // server-side in the `deployment_settings` view via
  // `Deployment.owner_has_advanced_settings_access`).
  if (advancedSettingsAvailable) {
    enterpriseFeatures = (
      <section id="advanced_settings">
        <h3 className="text-r8-xl text-r8-gray-12 font-semibold mb-05lh">
          Advanced settings
        </h3>

        <div className="space-y-lh">
          <label>
            <div className="flex items-center gap-1 text-base font-semibold mb-05lh">
              Setup timeout
              <IconTooltip
                icon="Question"
                weight="light"
                tooltipText={`Custom setup timeout for your deployment. Range: ${MIN_SETUP_TIMEOUT_MINUTES} ${
                  MIN_SETUP_TIMEOUT_MINUTES === 1 ? "minute" : "minutes"
                } to ${MAX_SETUP_TIMEOUT_MINUTES / 60} ${
                  MAX_SETUP_TIMEOUT_MINUTES / 60 === 1 ? "hour" : "hours"
                }.`}
                anchorClass="flex-shrink-0 inline"
              />
            </div>
            <div className="flex items-center gap-2">
              <TextField
                id="setup_timeout"
                ref={setupTimeoutInputRef}
                type="number"
                name="setup_timeout"
                value={setupTimeoutValue}
                onChange={onSetupTimeoutChange}
                placeholder="Enter timeout"
                className="w-32 block bg-white dark:bg-r8-gray-1 border border-r8-gray-12 p-05lh"
                step={setupTimeoutUnit === "hours" ? 0.5 : 1}
                onWheel={onWheel}
              />
              <NativeSelect
                id="setup_timeout_unit"
                name="setup_timeout_unit"
                value={setupTimeoutUnit}
                onChange={onSetupTimeoutUnitChange}
                className="block bg-white dark:bg-r8-gray-1 border border-r8-gray-12 p-05lh"
              >
                <option value="minutes">
                  {setupTimeoutValue === "1" ? "minute" : "minutes"}
                </option>
                <option value="hours">
                  {setupTimeoutValue === "1" ? "hour" : "hours"}
                </option>
              </NativeSelect>
            </div>
          </label>

          <div>
            <Checkbox
              type="checkbox"
              checked={hasSetupAlertsEnabled}
              onChange={(e) => setSetupAlertsEnabled(e.target.checked)}
              name="setup_alerts_enabled"
              id="setup_alerts_checkbox"
              disabled={setupAlertsEmail === null}
            />
            <label
              htmlFor="setup_alerts_checkbox"
              className="bg-white dark:bg-r8-gray-1 p-05lh mb-2"
            >
              Enable notifications for setup failure
            </label>
            {setupAlertsEmail ? (
              <p className="text-r8-sm text-r8-gray-11 mt-05lh">
                Alerts will be sent to {setupAlertsEmail}.{" "}
                <a href={route("account_email_settings")}>Update email</a>.
              </p>
            ) : (
              <p className="text-r8-sm text-r8-gray-11 mt-05lh">
                <a href={route("account_email_settings")}>
                  Set an email address
                </a>{" "}
                to enable setup failure notifications.
              </p>
            )}
          </div>

          <ScalingPoliciesField
            direction="scale_in"
            label="Scale-in"
            description="Limit how fast this deployment can scale in."
            control={scaleInPolicies}
          />
          <StabilizationWindowField
            direction="scale_in"
            label="Scale-in stabilization window"
            description="How long to wait, after a scale-in decision, before actually removing instances. Higher values reduce flapping but slow reaction to drops in traffic."
            control={scaleInStabilization}
            onWheel={onWheel}
          />
          <ScalingPoliciesField
            direction="scale_out"
            label="Scale-out"
            description="Limit how fast this deployment can scale out."
            control={scaleOutPolicies}
          />
          <StabilizationWindowField
            direction="scale_out"
            label="Scale-out stabilization window"
            description="How long to wait, after a scale-out decision, before actually adding instances. Higher values reduce flapping but slow reaction to spikes in traffic."
            control={scaleOutStabilization}
            onWheel={onWheel}
          />
        </div>
      </section>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-8">
      <div className="content-container">
        <form
          className="space-y-2lh"
          id="settings-form"
          onSubmit={onFormSubmit}
          noValidate={true}
        >
          <section id="version" className="scroll-mt-28">
            <h3 className="text-r8-xl text-r8-gray-12 font-semibold mb-1">
              Version
            </h3>
            <VersionPicker
              name="version"
              versions={versions}
              value={currentVersion}
              onVersionChange={setVersion}
            />
          </section>
          <section id="hardware">
            <h3 className="text-r8-xl text-r8-gray-12 font-semibold mb-1">
              Hardware
            </h3>

            <Ariakit.RadioProvider
              value={hardwareSku}
              setValue={(val: string) => {
                setHardwareSku(val);
              }}
            >
              <Ariakit.RadioGroup
                aria-label="Hardware"
                className="flex flex-col gap-4"
              >
                {hardwareOptions.map((h, idx) => (
                  <HardwareOption
                    key={idx}
                    hardware={h}
                    selected={hardwareSku === h.public_sku}
                  />
                ))}
              </Ariakit.RadioGroup>
            </Ariakit.RadioProvider>
          </section>

          <section id="autoscaling">
            <h3 className="text-r8-xl text-r8-gray-12 font-semibold mb-05lh">
              Autoscaling
            </h3>

            <fieldset className="flex flex-col md:grid grid-cols-2 gap-2lh">
              <label>
                <div className="flex items-center justify-between mb-05lh">
                  <div className="block text-base font-semibold">
                    Minimum instances
                  </div>
                  <div className="text-sm text-r8-gray-11">
                    <TooltipProvider>
                      <TooltipAnchor>
                        {limits.minInstances.min} min /{" "}
                        <span
                          className={
                            hasPinnedMaxMinInstances
                              ? "border-dotted border-r8-gray-6 border-b cursor-pointer"
                              : ""
                          }
                        >
                          {limits.minInstances.currentMax} max
                        </span>
                      </TooltipAnchor>
                      {hasPinnedMaxMinInstances ? (
                        <Tooltip>
                          <span>Restricted by maximum instances</span>
                          <TooltipArrow />
                        </Tooltip>
                      ) : null}
                    </TooltipProvider>
                  </div>
                </div>

                <input
                  type="number"
                  id="min"
                  name="min_instances"
                  value={minInstancesValue}
                  required
                  placeholder="Enter a number"
                  className="w-full block bg-white dark:bg-r8-gray-1 border border-r8-gray-12 p-05lh mb-2"
                  min={limits.minInstances.min}
                  max={limits.minInstances.currentMax}
                  step="1"
                  onChange={onMinInstancesChange}
                  onWheel={onWheel}
                />

                <span>
                  Keep one or more instances running at all times to avoid{" "}
                  <a href="https://replicate.com/docs/reference/how-does-replicate-work#cold-boots">
                    cold boots
                  </a>
                  .
                </span>
              </label>

              <label>
                <div className="flex items-center justify-between mb-05lh">
                  <span className="block text-base font-semibold">
                    Maximum instances
                  </span>
                  <span className="text-sm text-r8-gray-11">
                    {limits.maxInstances.min} min / {limits.maxInstances.max}{" "}
                    max
                  </span>
                </div>

                <input
                  type="number"
                  id="max"
                  required
                  placeholder="Enter a number"
                  name="max_instances"
                  value={maxInstancesValue}
                  className="w-full block bg-white dark:bg-r8-gray-1 border border-r8-gray-12 p-05lh mb-2"
                  min={limits.maxInstances.min}
                  max={limits.maxInstances.max}
                  step="1"
                  onChange={onMaxInstancesChange}
                  onWheel={onWheel}
                />

                <span>
                  Limit the number of concurrent predictions to run at once.
                </span>
              </label>
            </fieldset>

            {showIncreaseCapacityCTA && (
              <p className="mt-lh px-6 py-4 bg-r8-gray-3">
                Want to run more instances concurrently?{" "}
                <a href={route("support")}>Contact us</a>.
              </p>
            )}
          </section>
          {enterpriseFeatures}
        </form>
        <hr className="my-8" />
        <section id="disable-deployment">
          <DisableDeploymentUI deployment={deployment} />
        </section>
        <hr className="my-8" />
        <section id="rename-deployment">
          <RenameDeploymentForm
            deployment={deployment}
            disabledReason={renameDisabledReason}
          />
        </section>
        <hr className="my-8" />
        <section id="delete-deployment" className="md:mb-16">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex-shrink-0 w-96">
              <h3 className="text-r8-xl text-r8-gray-12 font-semibold">
                Delete deployment
              </h3>
              <p className="r8-text-sm">
                If you no longer need your deployment, you can delete it.
              </p>
            </div>
            <div>
              <DeleteDeploymentButton
                deployment={deployment}
                disabledReason={deleteDisabledReason}
              />
            </div>
          </div>
        </section>
      </div>

      <aside className="relative flex-shrink-0">
        <div className="top-24 sticky w-full md:w-80 lg:w-96">
          <h3 className="text-r8-xl text-r8-gray-12 font-semibold mb-05lh">
            Pricing summary
          </h3>
          <div className="border p-4">
            <dl className="grid grid-cols-2 gap-x-lh tabular-nums">
              <dt className="font-bold">Base</dt>
              <dd className="flex justify-end overflow-hidden min-w-0 text-right tabular-nums">
                <span
                  className="truncate"
                  data-value={minCost.raw}
                  data-currency="USD"
                >
                  {minCost.formatted}
                </span>
                <span className="flex-shrink-0">/day</span>
              </dd>
              <dd className="text-sm text-r8-gray-11 col-span-2 mb-lh md:max-w-xs">
                The base cost for the configured minimum instances, before
                autoscaling
              </dd>
              <dt className="font-bold">Maximum</dt>
              <dd className="flex justify-end overflow-hidden min-w-0 text-right tabular-nums">
                <span
                  className="truncate"
                  data-value={maxCost.raw}
                  data-currency="USD"
                >
                  {maxCost.formatted}
                </span>
                <span className="flex-shrink-0">/day</span>
              </dd>
              <dd className="text-sm text-r8-gray-11 col-span-2">
                Your bill won't exceed this amount
              </dd>
            </dl>
          </div>
          <div className="mt-4 space-y-4">
            {errorDetailsList ? (
              <ul className="p-4 text-r8-sm text-r8-red-10 bg-r8-red-3">
                {errorDetailsList}
              </ul>
            ) : null}
            {saveBanner}
            <Button
              // Please don't remove or tweak this ID. It's used here
              // so that we can submit the form from outside the form markup.
              form="settings-form"
              type="submit"
              className="w-full"
            >
              Save settings
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}
